import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import HomeIcon from '@mui/icons-material/HomeOutlined';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import CarIcon from '@mui/icons-material/DirectionsCarOutlined';
import ExitIcon from '@mui/icons-material/LogoutOutlined';
import OfferIcon from '@mui/icons-material/LocalOfferOutlined';
import NotifyIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { api } from 'common';
import { useTranslation } from "react-i18next";
import AccountCircleIcon from '@mui/icons-material/AccountCircleOutlined';
import ContactPhoneIcon from '@mui/icons-material/ContactPhoneOutlined';
import ViewListIcon from '@mui/icons-material/ReceiptLongOutlined';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import EmojiPeopleIcon from '@mui/icons-material/PeopleAltOutlined';
import AssessmentIcon from '@mui/icons-material/BarChartOutlined';
import MoneyIcon from '@mui/icons-material/PaymentsOutlined';
import PhoneIphoneIcon from '@mui/icons-material/CreditCardOutlined';
import SettingsIcon from '@mui/icons-material/TuneOutlined';
import SosIcon from '@mui/icons-material/WarningAmberOutlined';
import AnnouncementOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import ElectricBikeIcon from '@mui/icons-material/ElectricBikeOutlined';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import { Helmet } from 'react-helmet-async';
import { FONT_FAMILY } from "../common/sharedFunctions";

const drawerWidth = 270;

export default function ResponsiveDrawer(props) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const location = useLocation();
  const { signOff } = api;
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const dispatch = useDispatch();

  const [role, setRole] = useState(null);

  useEffect(() => {
    if (auth.profile && auth.profile.usertype) {
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

  const getPageTitle = () => {
    const path = location.pathname;
    const appName = settings?.appName || 'HIILLA';
    if (path === '/dashboard') return `Dashboard | ${appName} Operations`;
    if (path.startsWith('/bookings/bookingdetails')) return `Booking Details | ${appName}`;
    if (path === '/bookings') return `Live Orders & Bookings | ${appName}`;
    if (path === '/addbookings') return `Manual Dispatch | ${appName}`;
    if (path === '/users/0') return `Customers | ${appName}`;
    if (path === '/users/1') return `Riders & Drivers | ${appName}`;
    if (path === '/users/2') return `Fleet Operators | ${appName}`;
    if (path === '/users/3') return `Super Admins | ${appName}`;
    if (path.startsWith('/users')) return `User Directory | ${appName}`;
    if (path === '/cartypes') return `Vehicle Categories | ${appName}`;
    if (path === '/parceltypes') return `Unified Parcel & Slabs Config | ${appName}`;
    if (path === '/cars') return `Fleet Vehicles | ${appName}`;
    if (path === '/promos') return `Promo Codes & Discounts | ${appName}`;
    if (path === '/notifications') return `Push Notifications | ${appName}`;
    if (path === '/addtowallet') return `Wallet Adjustments | ${appName}`;
    if (path === '/userwallet') return `My Wallet | ${appName}`;
    if (path === '/allreports') return `Financial Reports | ${appName}`;
    if (path === '/withdraws') return `Withdrawal Requests | ${appName}`;
    if (path === '/sos') return `Emergency SOS Alerts | ${appName}`;
    if (path === '/complain') return `Customer Support | ${appName}`;
    if (path === '/settings') return `Platform Settings | ${appName}`;
    if (path === '/profile') return `My Profile | ${appName}`;
    return `${appName} | Logistics Console`;
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleDrawer = () => {
    setMobileOpen(false);
  };

  const LogOut = () => {
    dispatch(signOff());
  };

  const menuSections = [
    {
      title: 'OVERVIEW',
      items: [
        { name: t('dashboard_text', { defaultValue: 'Dashboard' }), url: '/dashboard', icon: <DashboardIcon fontSize="small" />, access: ['admin', 'fleetadmin'] },
        { name: t('book_now', { defaultValue: 'Book a Delivery' }), url: '/addbookings', icon: <HomeIcon fontSize="small" />, access: ['customer'] },
      ]
    },
    {
      title: 'OPERATIONS',
      items: [
        { name: t('booking_history', { defaultValue: 'Bookings & Orders' }), url: '/bookings', icon: <ViewListIcon fontSize="small" />, access: ['admin', 'fleetadmin', 'driver', 'customer'] },
        { name: t('addbookinglable', { defaultValue: 'Manual Dispatch' }), url: '/addbookings', icon: <ContactPhoneIcon fontSize="small" />, access: settings && settings.appCat === 'bidcab' ? ['customer'] : ['admin', 'fleetadmin', 'customer'] },
        { name: t('sos', { defaultValue: 'Emergency SOS' }), url: '/sos', icon: <SosIcon fontSize="small" />, access: ['admin'] },
        { name: t('complain', { defaultValue: 'Support & Complaints' }), url: '/complain', icon: <AnnouncementOutlinedIcon fontSize="small" />, access: ['admin'] },
      ]
    },
    {
      title: 'FLEET & USERS',
      items: [
        { name: t('user', { defaultValue: 'Users & Roles' }), url: '/users/0', icon: <EmojiPeopleIcon fontSize="small" />, access: ['admin', 'fleetadmin'] },
        { name: t('cars', { defaultValue: 'Fleet Vehicles' }), url: '/cars', icon: <CarIcon fontSize="small" />, access: ['admin', 'fleetadmin', 'driver'] },
        { name: t('car_type', { defaultValue: 'Vehicle Types' }), url: '/cartypes', icon: <ElectricBikeIcon fontSize="small" />, access: ['admin'] },
        { name: t('delivery_pricing_config', { defaultValue: 'Parcel & Slabs Config' }), url: '/parceltypes', icon: <InventoryIcon fontSize="small" />, access: ['admin'] },
        { name: t('push_notifications', { defaultValue: 'Push Broadcasts' }), url: '/notifications', icon: <NotifyIcon fontSize="small" />, access: ['admin'] },
      ]
    },
    {
      title: 'FINANCE & REPORTS',
      items: [
        { name: t('withdraws_web', { defaultValue: 'Withdrawals' }), url: '/withdraws', icon: <MoneyIcon fontSize="small" />, access: ['admin'] },
        { name: t('add_to_wallet', { defaultValue: 'Wallet Top-ups' }), url: '/addtowallet', icon: <AccountBalanceWalletIcon fontSize="small" />, access: ['admin'] },
        { name: t('report', { defaultValue: 'Financial Reports' }), url: '/allreports', icon: <AssessmentIcon fontSize="small" />, access: ['admin', 'fleetadmin'] },
        { name: t('promo', { defaultValue: 'Promo Discounts' }), url: '/promos', icon: <OfferIcon fontSize="small" />, access: ['admin'] },
        { name: t('my_wallet_tile', { defaultValue: 'My Wallet' }), url: '/userwallet', icon: <AccountBalanceWalletIcon fontSize="small" />, access: ['driver', 'customer', 'fleetadmin'] },
      ]
    },
    {
      title: 'SETTINGS',
      items: [
        { name: t('settings_title', { defaultValue: 'Platform Settings' }), url: '/settings', icon: <SettingsIcon fontSize="small" />, access: ['admin'] },
        { name: t('profile', { defaultValue: 'My Profile' }), url: '/profile', icon: <AccountCircleIcon fontSize="small" />, access: ['admin', 'fleetadmin', 'driver', 'customer'] },
      ]
    }
  ];

  const currentPath = location.pathname;

  const drawer = (
    <Box sx={{ backgroundColor: '#0B1120', height: '100%', display: 'flex', flexDirection: 'column', color: '#F8FAFC', WebkitFontSmoothing: 'antialiased' }}>
      {/* ── Brand Header ── */}
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Box
          component="img"
          src={require("../assets/img/logo192x192.png")}
          alt="Logo"
          sx={{ width: 42, height: 42, borderRadius: '10px', boxShadow: '0 4px 14px rgba(229, 35, 42, 0.4)' }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            noWrap
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 800,
              color: '#FFFFFF',
              letterSpacing: '0.5px',
              fontSize: '1.05rem',
              lineHeight: 1.2
            }}
          >
            {settings?.appName || 'HIILLA'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.4 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#10B981', boxShadow: '0 0 8px #10B981' }} />
            <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, color: '#94A3B8', fontSize: '0.72rem', fontWeight: 600 }}>
              {role === 'admin' ? 'Super Admin Console' : role === 'fleetadmin' ? 'Fleet Admin Portal' : role === 'driver' ? 'Driver Portal' : role === 'customer' ? 'Customer Portal' : 'Web Console'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Navigation List ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 2 }}>
        {role && menuSections.map((section, sIdx) => {
          const visibleItems = section.items.filter(item => item.access.includes(role));
          if (visibleItems.length === 0) return null;

          return (
            <Box key={section.title} sx={{ mb: 2.2 }}>
              <Typography
                variant="caption"
                sx={{
                  px: 1.5,
                  mb: 0.8,
                  display: 'block',
                  fontFamily: FONT_FAMILY,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  letterSpacing: '1px',
                  color: '#64748B'
                }}
              >
                {section.title}
              </Typography>

              <List disablePadding>
                {visibleItems.map((item) => {
                  const isActive = currentPath === item.url || (item.url !== '/' && currentPath.startsWith(item.url));

                  return (
                    <ListItemButton
                      key={item.url}
                      component={Link}
                      to={item.url}
                      onClick={handleDrawer}
                      sx={{
                        borderRadius: '12px',
                        py: 0.95,
                        px: 1.5,
                        mb: 0.4,
                        color: isActive ? '#FFFFFF' : '#94A3B8',
                        backgroundColor: isActive ? '#E5232A' : 'transparent',
                        boxShadow: isActive ? '0 4px 14px rgba(229, 35, 42, 0.4)' : 'none',
                        transition: 'all 0.18s ease',
                        '&:hover': {
                          backgroundColor: isActive ? '#E5232A' : 'rgba(255, 255, 255, 0.06)',
                          color: '#FFFFFF',
                          transform: 'translateX(3px)'
                        }
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 34,
                          color: isActive ? '#FFFFFF' : '#94A3B8'
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{
                          fontFamily: FONT_FAMILY,
                          fontWeight: isActive ? 700 : 500,
                          fontSize: '0.88rem',
                          textAlign: isRTL === 'rtl' ? 'right' : 'left'
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>

      {/* ── Bottom Profile Card ── */}
      <Box sx={{ p: 1.5, borderTop: '1px solid rgba(255,255,255,0.08)', backgroundColor: 'rgba(0,0,0,0.25)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.04)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
            <Avatar
              src={auth.profile?.profile_image}
              sx={{ width: 36, height: 36, border: '1.5px solid rgba(255,255,255,0.2)', bgcolor: '#E5232A', fontSize: '0.85rem', fontWeight: 700 }}
            >
              {auth.profile?.firstName ? auth.profile.firstName.charAt(0) : 'A'}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#FFFFFF', fontSize: '0.82rem', lineHeight: 1.2 }}>
                {auth.profile?.firstName || 'Administrator'}
              </Typography>
              <Typography variant="caption" noWrap sx={{ fontFamily: FONT_FAMILY, color: '#94A3B8', fontSize: '0.68rem', textTransform: 'uppercase', fontWeight: 600 }}>
                {role === 'admin' ? 'Super Admin' : role || 'User'}
              </Typography>
            </Box>
          </Box>

          <Tooltip title={t('logout', { defaultValue: 'Logout' })}>
            <IconButton
              size="small"
              onClick={LogOut}
              sx={{
                color: '#94A3B8',
                p: 0.8,
                borderRadius: '8px',
                '&:hover': { color: '#EF4444', bgcolor: 'rgba(239, 68, 68, 0.12)' }
              }}
            >
              <ExitIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex', direction: isRTL === 'rtl' ? 'rtl' : 'ltr', backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <CssBaseline />
      <style>
        {`
          ::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          ::-webkit-scrollbar-thumb {
            background-color: #CBD5E1;
            border-radius: 6px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background-color: #94A3B8;
          }
          ::-webkit-scrollbar-track {
            background-color: transparent;
          }
        `}
      </style>
      <Helmet>
        <title>{getPageTitle()}</title>
        <meta name="description" content="HIILLA Logistics & Fleet Dispatch Management Platform." />
      </Helmet>

      {/* ── Modern Top Navigation Header ── */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid #E2E8F0',
          color: '#0F172A',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1, display: { sm: 'none' }, color: '#0F172A' }}
            >
              <MenuIcon />
            </IconButton>

            <Typography
              variant="h6"
              noWrap
              sx={{
                fontFamily: FONT_FAMILY,
                fontWeight: 800,
                color: '#0F172A',
                fontSize: { xs: '1rem', sm: '1.15rem' }
              }}
            >
              {settings?.appName || 'HIILLA Delivery Logistics'}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              size="small"
              label={new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              sx={{
                fontFamily: FONT_FAMILY,
                fontWeight: 600,
                backgroundColor: '#F1F5F9',
                color: '#475569',
                display: { xs: 'none', md: 'inline-flex' }
              }}
            />

            <Box
              component={Link}
              to="/profile"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                p: 0.5,
                pr: 1.5,
                borderRadius: '24px',
                border: '1px solid #E2E8F0',
                backgroundColor: '#FFFFFF',
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: '#CBD5E1', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }
              }}
            >
              <Avatar
                src={auth.profile?.profile_image}
                sx={{ width: 28, height: 28, bgcolor: '#E5232A', fontSize: '0.75rem', fontWeight: 700 }}
              >
                {auth.profile?.firstName ? auth.profile.firstName.charAt(0) : 'A'}
              </Avatar>
              <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#0F172A', fontSize: '0.8rem', display: { xs: 'none', sm: 'block' } }}>
                {auth.profile?.firstName || 'Admin'}
              </Typography>
            </Box>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* ── Sidebar Navigation Drawer ── */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="admin navigation drawer"
      >
        <Drawer
          container={container}
          variant="temporary"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, border: 'none' }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, border: 'none' }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* ── Main Content Area ── */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2, md: 3 },
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          maxWidth: '100%',
          overflowX: 'hidden',
          backgroundColor: '#F8FAFC',
          minHeight: '100vh',
          boxSizing: 'border-box'
        }}
      >
        <Toolbar />
        {props.children}
      </Box>
    </Box>
  );
}

